
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useI18n } from "src/utils/lni18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { atom, useAtom } from "jotai";
import courseService, { allPartsInCourseAtom, oneCourseatom, oneThemeAtom } from "../CourseService";
import coursePartCollectionService, { allFoundPartsAtom } from "src/coursepart/CoursePartCollectionService";
import { debouncer } from "src/utils/debouncer";
import { ErrorService } from "src/components/Error/ErrorService";
import { PartSmall } from "./PartSmall";
import CoursePartListAndSelect from "./CoursePartListAndSelect";
import CoursePartFindByAuthor from "./CoursePartFindByAuthor";
import { Active, DndContext, DragEndEvent, DragOverEvent, DragOverlay, DropAnimation, defaultDropAnimationSideEffects } from "@dnd-kit/core";
import { SortableContext, } from "@dnd-kit/sortable";
import { useThemeDragNDrop } from "./UseThemeDragNDrop";
import { createPortal } from "react-dom";
import { PartWithThumbnail } from "../domain/CourseEditModel";
import { useImmerAtom } from "jotai-immer";
import { arrayMoveMutate } from "src/utils/ArrayMethods";
import { partByAuthorAtom } from "src/coursepart/CoursePartService";
import { Item, ItemParams, Menu, useContextMenu } from "react-contexify";
import { useInput } from "src/components/Modalservice/UseInput";
import { useConfirm } from "src/components/Modalservice/UseConfirm";

enum TabType {
    mine,
    others,
    byAuthor
}

interface PartWithThumbnailDummy extends PartWithThumbnail {
    dummy?: boolean
}

const MENU_ID = "part-menu-id";

export const isAnyPartDraggingAtom = atom(false);
const searchTextAtom = atom("");

const dummyPart: PartWithThumbnailDummy = {
    Id: "dummy",
    ConnectedWithCourse: false,
    Dirty: false,
    Name: "d",
    Selected: false,
    Text: "",
    dummy: true
}

const EditThemeParts = () => {

    const { courseId, themeId } = useParams();
    const [partsData] = useAtom(allFoundPartsAtom);
    const debounce = useRef<debouncer | undefined>(undefined);
    const { languageService: t } = useI18n();
    const { state } = useLocation();
    const confirm = useConfirm();
    const input = useInput();

    const [searchText, setSearchText] = useAtom(searchTextAtom);
    const [isAnyPartDragging, setIsAnyPartDragging] = useAtom(isAnyPartDraggingAtom);

    const [_, setCourseDataFunction] = useImmerAtom(useMemo(() => oneCourseatom(courseId || ""), [courseId]));

    const [theme] = useAtom(useMemo(() => oneThemeAtom(courseId!, themeId!), [courseId, themeId]));

    const [insertAt, setInsertAt] = useState<number | undefined>(undefined);

    const [authorParts] = useAtom(partByAuthorAtom);

    const navigate = useNavigate();

    const [tabType, setTabType] = useState(TabType.mine);

    const [hideDeleteItem, setHideDeleteItem] = useState(false);

    const allThemes = "allThemes";
    const { sensors, active, setActive } = useThemeDragNDrop(courseId!, allThemes);

    const { show } = useContextMenu({
        id: MENU_ID
    });


    const [partIds] = useAtom(useMemo(() => allPartsInCourseAtom(courseId || ""), [courseId]));


    useEffect(() => {
        debounce.current = new debouncer(coursePartCollectionService.fetchCourseParts, 500, coursePartCollectionService);
        return () => debounce.current?.flush();
    }, [])

    const updateSearch = (e: React.ChangeEvent<HTMLInputElement>) => {

        setSearchText(e.currentTarget.value);
        if (e.currentTarget.value.length > 2) {
            var text = e.currentTarget.value;
            debounce.current?.debounce(text, true);
        }
        else {
            coursePartCollectionService.fetchCourseParts("", true);
        }
    }

    function addNewPart(): void {

        input(t.getText("courseparts.createnew"), <div>{t.getText("mail.noname")}</div>)
            .then(async (text: string) => {
                try {
                    const part = await courseService.addNewPart(text, courseId!, theme!.Id);
                    if (!part) throw new Error("part not created");
                } catch (error) {
                    console.error(error);
                    ErrorService.setError({ message: t.getText("could.not.createpart"), header: t.getText("error"), when: new Date() });

                }
            }).catch((e: Error) => { });
    }

    const dropAnimation: DropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    const getPartById = useCallback((active: Active) => {

        const id = active.id
        const containerId = active.data?.current?.sortable?.containerId

        if (id === dummyPart.Id) return dummyPart;

        let part: PartWithThumbnail | undefined = undefined;

        if (!!containerId && (containerId === theme?.Id)) part = theme?.Parts.find(p => p.Id === id);

        if (!part) {
            part = partsData.OwnedParts.find(p => p.Id === id)
                ?? partsData.EditParts.find(p => p.Id === id)
                ?? authorParts.find(p => p.Id === id);
        }
        return part;

    }, [theme, partsData, authorParts]);


    const dragEndSort = useCallback(async (e: DragEndEvent) => {

        let doSave = false;
        setCourseDataFunction(courseData => {
            if (courseData.Course.Themes) {
                const ths = courseData.Course.Themes;
                const theme = ths.find(t => t.Id === e.active?.data.current?.sortable.containerId);
                if (theme) {
                    arrayMoveMutate(theme.Parts, e.active.data.current?.sortable.index, e.over?.data.current?.sortable.index);
                    doSave = true;
                }
            }
        });
        if (doSave && courseId) courseService.saveCourse(courseId);

    }, [courseId, theme]);


    const dragEnd = useCallback(async (ev: DragEndEvent) => {

        setIsAnyPartDragging(false);
        try {
            // no item on top of an other. No Action please.
            if ((ev.collisions?.length ?? 0) === 0) return;

            // if we have a container ID in the active, then it is a sort event.
            const containerId = ev.active.data?.current?.sortable?.containerId;
            if (containerId) {
                await dragEndSort(ev);
                return;
            }

            // here we have a drag into theme from the other parts searched by user
            const part = getPartById(ev.active);

            if (part && theme && courseId) {
                // insertAt  should be set to correct index in the dragOver events
                const overId = ev.collisions![0].id;
                const index = insertAt ?? theme.Parts.findIndex(p => p.Id === overId);
                courseService.addPart(courseId, theme.Id, part, index);
            }
            setActive(null);
            setInsertAt(undefined);
        } finally {
            setActive(null);
        }

    }, [insertAt, getPartById, theme, courseId, dragEndSort, setActive]);


    const dragOver = useCallback((e: DragOverEvent) => {

        /**
         * if no e.active.data?.current, we have a drag into event
         */
        if (!e.active.data?.current) {
            if (e.collisions?.length) {

                /**
                 * we have a collision. Let's find out where we want to add the dragged part
                 */

                const overId = e.collisions[0].id;

                let overIndex = theme?.Parts.findIndex(p => p.Id === overId);
                if (overIndex !== undefined) {
                    /**
                     * overIndex is index of the part in theme we are over
                     * if we are over two parts and not at the end 
                     * set overIndex += 1  so we hit between the parts.
                     * 
                     */

                    if (e.collisions.length > 1) { // && overIndex < (theme?.Parts?.length ?? 0)) {
                        overIndex += 1;
                    }

                    // save the index to be used at a drop later in dragEnd() method
                    setInsertAt(overIndex);
                    return;
                }
            }
        }
        setInsertAt(undefined);



    }, [theme]);

    const getThemePartsWithOptionalMarker = useCallback(() => {

        let parts = [...(theme?.Parts ?? [])];
        if (parts.length === 0) return [dummyPart];
        if (insertAt !== undefined) {
            parts.splice(insertAt, 0, { Id: "mark", Name: "Marker", ImageUrl: "" } as PartWithThumbnailDummy);
        }

        return parts;

    }, [theme, insertAt])

    const showContextMenu = (e: React.MouseEvent, part: PartWithThumbnail) => {
        e.preventDefault();

        setHideDeleteItem(partIds[part?.Id] === undefined);

        requestAnimationFrame(() => {
            show({
                event: e,
                props: part
            });
        });


    };

    async function handleItemClick(params: ItemParams<PartWithThumbnail, any>) {
        const { id, props }: ItemParams<PartWithThumbnail, any> = params;
        if (!props) return;
        switch (id) {

            case "delete":

                removePart(props);
                break;
            case "open":
                {
                    const url = partUrl(props.Id);
                    if (url) navigate(url);
                    break;
                }
        }


    }

    /** Where a part opens: inside this course when it is in it, on its own otherwise. */
    function partUrl(partId: string): string {
        if (partIds[partId] !== undefined) {
            return courseService.getUrlToPartInOpenCourse(partId) ?? "";
        }
        return `/coursepart/${partId}/texts`;
    }

    function removePart(part: PartWithThumbnail): void {

        if (!courseId || !themeId) return;

        confirm("md", t.getText("remove"),
                <div className="preserve-white" >{t.getText("remove.x.from.course", `${part.Name}`)}</div>)
            .then(async (res: boolean) => {
                if (res) {
                    await courseService.removePart(courseId, themeId, part.Id);
                }
            }).catch((e: Error) => { });

    }



    if (!theme) return <></>;

    return (<>

        <DndContext
            sensors={sensors}
            onDragStart={({ active }) => {
                setIsAnyPartDragging(true);
                setActive(active);
            }}
            onDragOver={dragOver}
            onDragEnd={dragEnd}
            onDragCancel={() => {
                setActive(null);
            }} >

            <div className={`d-flex flex-wrap ${insertAt ? "bg-primary-lightest" : ""}`}>
                <SortableContext items={getThemePartsWithOptionalMarker().map(p => p.Id)} id={theme.Id}>
                    {getThemePartsWithOptionalMarker().map(p => p as PartWithThumbnailDummy).map((part) => (
                        <PartSmall onContextMenu={(e) => showContextMenu(e, part)} key={part.Id} part={part} dummy={part.dummy} to={partUrl(part.Id) || undefined} />
                    ))}

                </SortableContext>

            </div>

            <div className="p-5">

                {!state?.readOnly &&
                    <div className="mb-3 mt-5">

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="custom-tab-heading-list mt-3" >
                                <button type="button" className={`${tabType === TabType.mine ? "active" : ""}`} onClick={() => setTabType(TabType.mine)}>{t.getText("your.courseparts")}</button>
                                <button type="button" className={`${tabType === TabType.others ? "active" : ""}`} onClick={() => setTabType(TabType.others)}>{t.getText("others")}</button>
                                <button type="button" className={`${tabType === TabType.byAuthor ? "active" : ""}`} onClick={() => setTabType(TabType.byAuthor)}>{t.getText("search.by.author")}</button>

                            </div>
                            <button onClick={addNewPart} className="ms-3 btn btn-small btn-primary">{t.getText("courseparts.createnew")}</button>
                        </div>

                        {tabType === TabType.mine && (
                            <CoursePartListAndSelect
                                themeParts={partIds}
                                onContextMenu={showContextMenu}
                                partUrl={partUrl}
                                searchText={searchText} textChange={updateSearch} items={partsData.OwnedParts} />
                        )}

                        {tabType === TabType.others && (

                            <CoursePartListAndSelect
                                themeParts={partIds}
                                onContextMenu={showContextMenu}
                                partUrl={partUrl}
                                searchText={searchText} textChange={updateSearch} items={partsData.EditParts} />
                        )}

                        {tabType === TabType.byAuthor && (
                            <CoursePartFindByAuthor onContextMenu={showContextMenu} themeParts={partIds} partUrl={partUrl} />
                        )}



                    </div>
                }



            </div>


            {createPortal(
                <DragOverlay adjustScale={false} dropAnimation={dropAnimation} modifiers={[]} >

                    {active && active.id && getPartById(active)
                        ? <PartSmall onContextMenu={() => { }} dragged={true} part={getPartById(active)!} />
                        : null}
                </DragOverlay>,
                document.body
            )}


        </DndContext>

        <Menu id={MENU_ID}>

            <Item id="open" onClick={handleItemClick}>
                {t.getText("open")}
            </Item>

            <Item id="delete" hidden={hideDeleteItem} onClick={handleItemClick}>
                {t.getText("remove")}
            </Item>


        </Menu>

    </>

    )
}

export default EditThemeParts;
